import React, { useCallback, useMemo, useState } from 'react';
import Image from 'frontastic/lib/image';
import Modal from 'components/commercetools-ui/atoms/modal';
import Slider from 'components/commercetools-ui/atoms/slider';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, hugeDesktop } from 'helpers/utils/screensizes';
import { NationalBook } from 'fonts';
import { sanitizeHtml } from '../helpers/rendering';
import type { Milestone } from './types';

/** Rotate so the clicked milestone leads the modal's slider, then the rest in order. */
function rotate<T>(items: T[], by: number): T[] {
  if (!items.length) return items;
  const offset = ((by % items.length) + items.length) % items.length;
  return items.slice(offset).concat(items.slice(0, offset));
}

/**
 * One point on the timeline: a round image sitting on the rule, with the year and a line of copy.
 *
 * On the desktop rule the label is absolutely positioned above or below, per the milestone's
 * `desktop_image_alignment` — that alternation is what stops neighbouring labels colliding. On the
 * vertical mobile rule the label always sits to the right of the image.
 */
export default function MilestoneTile({
  milestone,
  milestones,
  index,
}: {
  milestone: Milestone;
  milestones: Milestone[];
  index: number;
}) {
  const [isHugeDesktop] = useMediaQuery(hugeDesktop);
  const [isDesktop] = useMediaQuery(desktop);
  const [isOpen, setIsOpen] = useState(false);
  const close = useCallback(() => setIsOpen(false), []);

  const above = milestone?.desktop_image_alignment === 'Top';
  const detail = milestone?.content_with_icon?.[0];
  const hasDetail = Boolean(detail?.title || detail?.content || detail?.icon?.url);
  const slides = useMemo(() => rotate(milestones, index), [milestones, index]);

  const label = (
    <>
      {milestone?.title && (
        <span className="text-20 font-semibold text-primary-black 3xl:text-26">{milestone.title}</span>
      )}
      {milestone?.description && (
        <div
          className={`text-12 font-semibold text-[#333] 3xl:text-16 ${NationalBook.className}`}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(milestone.description) }}
        />
      )}
    </>
  );

  return (
    <>
      <div
        className={`relative flex ${
          above ? 'flex-col' : 'flex-col-reverse'
        } items-start px-48 py-24 lg:items-center lg:px-0 lg:py-80 3xl:py-100 ${hasDetail ? 'hover:cursor-pointer' : ''}`}
      >
        {isDesktop && (
          <div className={`absolute z-10 text-center ${above ? 'top-15 3xl:top-10' : 'bottom-15 3xl:bottom-10'}`}>
            {label}
          </div>
        )}
        <div className="flex items-center gap-24">
          {milestone?.image?.url && (
            <Image
              src={encodeURI(milestone.image.url)}
              alt={milestone.image.description ?? milestone?.title ?? ''}
              sizes="100vw"
              width={100}
              height={100}
              style={{
                width: '100%',
                height: 'auto',
                maxWidth: isHugeDesktop ? '120px' : '85px',
                minWidth: isHugeDesktop ? '120px' : '85px',
                objectFit: 'cover',
              }}
              onClick={hasDetail ? () => setIsOpen(true) : undefined}
            />
          )}
          {!isDesktop && <div>{label}</div>}
        </div>
      </div>

      {hasDetail && (
        <Modal
          isOpen={isOpen}
          onRequestClose={close}
          style={{ content: { backgroundColor: 'white', padding: '40px 40px 10px 40px' } }}
          closeButton
          className="border-transparent focus:border-transparent focus:outline-none focus:ring-0"
          closeTimeoutMS={200}
        >
          <Slider
            slidesPerView={1}
            allowArrowsOnTouchDevice
            innerArrows
            dots={false}
            loop
            arrows
            allowTouchMove
            nextButtonStyles={{ right: isDesktop ? 85 : 0, top: isDesktop ? '50%' : '38%', zIndex: 10, padding: '0px' }}
            prevButtonStyles={{ left: isDesktop ? 85 : 0, top: isDesktop ? '50%' : '38%', zIndex: 10, padding: '0px' }}
          >
            {slides.map((slide, i) => {
              const d = slide?.content_with_icon?.[0];
              return (
                <div className="flex flex-col items-center justify-center gap-24" key={i}>
                  {d?.title && <span className="text-20 font-semibold text-primary-black 3xl:text-26">{d.title}</span>}
                  {d?.icon?.url && (
                    <Image
                      src={encodeURI(d.icon.url)}
                      alt={d.icon.description ?? d?.title ?? ''}
                      sizes="100vw"
                      width={100}
                      height={100}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxWidth: isHugeDesktop ? '350px' : '200px',
                        minWidth: isHugeDesktop ? '350px' : '200px',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  {d?.content && (
                    <div
                      className={`mt-20 h-[7rem] text-14 font-medium text-[#333] lg:h-[5rem] 3xl:text-16 ${NationalBook.className}`}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(d.content) }}
                    />
                  )}
                </div>
              );
            })}
          </Slider>
        </Modal>
      )}
    </>
  );
}
